import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Flame, Send, LinkIcon } from "lucide-react";
import { createRoast } from "@/hooks/useStore";
import { useApp } from "@/App";
import { toast } from "sonner";

export function SubmitPage() {
	const { currentUser, navigate, showLogin } = useApp();
	const [url, setUrl] = useState("");
	const [copy, setCopy] = useState("");
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!currentUser) {
			showLogin();
			return;
		}
		if (!copy.trim()) {
			toast.error("Please paste your landing page copy");
			return;
		}
		setLoading(true);
		const roast = await createRoast(currentUser.id, url.trim() || null, copy.trim());
		setLoading(false);
		toast.success("Your page has been roasted!");
		navigate({ name: "roast", id: roast.id });
	};

	return (
		<div className="mx-auto max-w-2xl space-y-6">
			<div>
				<h1 className="text-2xl font-bold tracking-tight">Submit for Roasting</h1>
				<p className="text-sm text-muted-foreground">
					Paste your landing page URL or copy. Get brutally honest feedback.
				</p>
			</div>

			<Card className="border-border/40 bg-card/50">
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-lg">
						<Flame className="h-5 w-5 text-orange-500" />
						Your Landing Page
					</CardTitle>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="url" className="flex items-center gap-1.5">
								<LinkIcon className="h-3.5 w-3.5" />
								URL (optional)
							</Label>
							<Input
								id="url"
								type="url"
								placeholder="https://your-landing-page.com"
								value={url}
								onChange={(e) => setUrl(e.target.value)}
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="copy">Landing Page Copy *</Label>
							<Textarea
								id="copy"
								placeholder="Paste your headline, subheadline, CTA, and key body copy here..."
								value={copy}
								onChange={(e) => setCopy(e.target.value)}
								rows={8}
								className="resize-none"
							/>
							<p className="text-xs text-muted-foreground">
								Include your headline, value proposition, CTA text, and any social proof.
							</p>
						</div>

						<Button
							type="submit"
							className="w-full gap-2"
							disabled={!copy.trim() || loading}
						>
							{loading ? (
								<>
									<Flame className="h-4 w-4 animate-pulse" />
									Generating Roast...
								</>
							) : (
								<>
									<Send className="h-4 w-4" />
									Roast My Page
								</>
							)}
						</Button>
					</form>
				</CardContent>
			</Card>

			<Card className="border-border/40 bg-card/30">
				<CardContent className="p-5">
					<h3 className="mb-2 font-semibold">What you'll get:</h3>
					<ul className="space-y-1.5 text-sm text-muted-foreground">
						<li className="flex items-center gap-2">
							<span className="text-orange-500">1.</span>
							Headline analysis with rewrite suggestions
						</li>
						<li className="flex items-center gap-2">
							<span className="text-orange-500">2.</span>
							CTA effectiveness score
						</li>
						<li className="flex items-center gap-2">
							<span className="text-orange-500">3.</span>
							Clarity and positioning feedback
						</li>
						<li className="flex items-center gap-2">
							<span className="text-orange-500">4.</span>
							Trust signal evaluation
						</li>
						<li className="flex items-center gap-2">
							<span className="text-orange-500">5.</span>
							Community rewrites from top analysts
						</li>
					</ul>
				</CardContent>
			</Card>
		</div>
	);
}
